import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const API = (path, opts) =>
  fetch(path, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, ...opts });

// Single pricing tier — £10 per user per month
const TIERS = [
  { key: 'standard', label: 'Standard', desc: '£10 per user / month · scales automatically as you add team members' },
];

const TRIAL_OPTIONS = [
  { value: 0, label: 'No trial — billing starts immediately' },
  { value: 1, label: '1 month free trial' },
  { value: 3, label: '3 months free trial' },
  { value: 6, label: '6 months free trial' },
];

const STEPS = ['Company Details', 'Admin User', 'Review & Create'];

export default function OwnerOnboarding() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(null); // { company, user }
  const [error, setError]     = useState('');
  const [emailConflict, setEmailConflict] = useState(null); // { id, name, role, status, company_name }
  const [freeingEmail, setFreeingEmail]   = useState(false);

  const [company, setCompany] = useState({
    name: '', website: '', subscription_tier: 'flat', trial_months: 3,
    referral_code: '', complimentary: false,
  });
  const [admin, setAdmin] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);

  const setC = (field, value) => setCompany(p => ({ ...p, [field]: value }));
  const setA = (field, value) => setAdmin(p => ({ ...p, [field]: value }));

  const validateStep = () => {
    if (step === 0) {
      if (!company.name.trim()) { setError('Company name is required'); return false; }
    }
    if (step === 1) {
      if (!admin.name.trim())  { setError('Admin name is required'); return false; }
      if (!admin.email.trim()) { setError('Admin email is required'); return false; }
      if (!admin.email.includes('@')) { setError('Enter a valid email'); return false; }
      if (!admin.password)     { setError('Password is required'); return false; }
      if (admin.password.length < 8) { setError('Password must be at least 8 characters'); return false; }
      if (admin.password !== admin.confirmPassword) { setError('Passwords do not match'); return false; }
    }
    return true;
  };

  const next = () => {
    setError('');
    setEmailConflict(null);
    if (!validateStep()) return;
    setStep(s => s + 1);
  };

  const submit = async () => {
    setError('');
    setEmailConflict(null);
    setSaving(true);
    const res = await API('/api/owner/onboard', {
      method: 'POST',
      body: JSON.stringify({
        company_name:      company.name,
        company_website:   company.website,
        subscription_tier: company.subscription_tier,
        trial_months:      company.complimentary ? 0 : company.trial_months,
        admin_name:        admin.name,
        admin_email:       admin.email,
        admin_phone:       admin.phone,
        admin_password:    admin.password,
        referral_code:     company.referral_code || undefined,
        complimentary:     company.complimentary,
      }),
    });
    const d = await res.json();
    setSaving(false);
    if (!res.ok) {
      // email_conflict comes with who the email actually belongs to (name/role/status/company)
      // so we can offer a real resolution instead of a dead-end "already exists" message.
      if (d.error === 'email_conflict' && d.existing_user) {
        setEmailConflict(d.existing_user);
        setError(d.message || 'This email already belongs to an existing user.');
      } else {
        setError(d.message || d.error || 'Failed to create company');
      }
      return;
    }
    setDone(d);
  };

  const freeEmailAndRetry = async () => {
    if (!emailConflict) return;
    setFreeingEmail(true);
    const res = await API(`/api/owner/users/${emailConflict.id}/free-email`, { method: 'PATCH' });
    const d = await res.json();
    setFreeingEmail(false);
    if (!res.ok) { setError(d.error || 'Failed to free up that email'); return; }
    setEmailConflict(null);
    await submit(); // the email is free now — retry onboarding with the same details
  };

  if (done) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '48px', maxWidth: '520px', width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Company Created!</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              <strong>{done.company.name}</strong> is now live on BrokerConnect{company.complimentary ? ' (complimentary — founding member)' : ''}.
            </p>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '20px', marginBottom: '24px', textAlign: 'left' }}>
              <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.8 }}>
                <div><strong>Company ID:</strong> <code style={{ fontSize: '12px' }}>{done.company.id}</code></div>
                <div><strong>Admin:</strong> {done.user.name} ({done.user.email})</div>
                <div style={{ color: '#6b7280', marginTop: '8px', fontSize: '12px' }}>
                  The admin can log in immediately and invite brokers.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => { setDone(null); setStep(0); setCompany({ name: '', website: '', subscription_tier: 'flat', trial_months: 3, referral_code: '', complimentary: false }); setAdmin({ name: '', email: '', phone: '', password: '', confirmPassword: '' }); }}
                style={btnOutline}>
                Onboard Another
              </button>
              <button onClick={() => navigate('/owner/companies')} style={btnPrimary}>
                View Companies →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Onboard New Company</h1>
        <p style={{ color: '#6b7280', marginBottom: '32px' }}>Create a broker company and their first admin user in one step</p>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '36px', maxWidth: '560px' }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: i < step ? '#10b981' : i === step ? '#1e40af' : '#e5e7eb',
                  color: i <= step ? '#fff' : '#9ca3af', fontWeight: 700, fontSize: '14px', marginBottom: '6px'
                }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '12px', fontWeight: i === step ? 600 : 400, color: i === step ? '#1e40af' : '#9ca3af', whiteSpace: 'nowrap' }}>{s}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ flex: 0, width: '48px', height: '2px', background: i < step ? '#10b981' : '#e5e7eb', marginTop: '15px' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '560px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {emailConflict ? (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#92400e', fontWeight: 700, marginBottom: '6px' }}>
                This email already belongs to an existing account
              </div>
              <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px', lineHeight: 1.5 }}>
                <strong>{emailConflict.name}</strong> — {emailConflict.role}
                {emailConflict.company_name ? ` at ${emailConflict.company_name}` : ''}, currently{' '}
                <strong>{emailConflict.status}</strong>. If they're coming back, reactivate that
                account instead of creating a new one. If this is a genuinely separate,
                unrelated signup, you can free up the email below.
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => navigate('/owner/users')} style={{ ...btnOutline, fontSize: '12px', padding: '8px 14px' }}>
                  Go reactivate them in Manage Users
                </button>
                {emailConflict.status !== 'active' && (
                  <button
                    type="button"
                    onClick={freeEmailAndRetry}
                    disabled={freeingEmail}
                    style={{ ...btnPrimary, fontSize: '12px', padding: '8px 14px', background: freeingEmail ? '#93c5fd' : '#1e40af' }}
                  >
                    {freeingEmail ? 'Freeing email…' : 'Free up this email & continue'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' }}>{error}</div>
          )}

          {/* Step 0: Company */}
          {step === 0 && (
            <div>
              <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '17px' }}>Company Details</h3>
              <Field label="Company Name *">
                <input value={company.name} onChange={e => setC('name', e.target.value)} placeholder="Acme Mortgages Ltd" style={inputStyle} />
              </Field>
              <Field label="Website">
                <input value={company.website} onChange={e => setC('website', e.target.value)} placeholder="https://acmemortgages.co.uk" style={inputStyle} />
              </Field>
              <Field label="Pricing">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', border: '2px solid #1e40af', borderRadius: '10px', background: '#eff6ff' }}>
                  <span style={{ fontSize: '22px' }}>💳</span>
                  <span>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: '15px', color: '#111827' }}>£10 per user / month</span>
                    <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: 2 }}>Billed monthly · scales automatically as you add team members · cancel any time</span>
                  </span>
                </div>
              </Field>
              <Field label="Free Trial">
                <select value={company.trial_months} onChange={e => setC('trial_months', parseInt(e.target.value, 10))} disabled={company.complimentary} style={{ ...inputStyle, opacity: company.complimentary ? 0.5 : 1 }}>
                  {TRIAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0' }}>
                  No fees accrue during the trial. You'll see a warning on your dashboard 14 days before it ends.
                </p>
              </Field>
              <Field label="Referral Code (optional)">
                <input value={company.referral_code} onChange={e => setC('referral_code', e.target.value.toUpperCase())} placeholder="e.g. ABC12345" style={{ ...inputStyle, textTransform: 'uppercase' }} />
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0' }}>
                  If another company referred this one, enter their code here — credits the referrer £2.50/month while this company stays active.
                </p>
              </Field>
              <Field label=" ">
                <div style={{
                  padding: '12px',
                  backgroundColor: company.complimentary ? '#ecfdf5' : '#fff',
                  border: `1px solid ${company.complimentary ? '#10b981' : '#d1d5db'}`,
                  borderRadius: '8px',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}>
                    <input type="checkbox" checked={company.complimentary} onChange={e => setC('complimentary', e.target.checked)} style={{ marginTop: '3px' }} />
                    <span>
                      Complimentary — founding member (no billing)
                      <div style={{ fontWeight: 400, fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Sets status to "Complimentary" instead of trial/active — no free-trial countdown, never billed until manually changed.
                        Gated on the separate Founding Member agreement (no fees wording) instead of the standard paid one.
                      </div>
                    </span>
                  </label>
                </div>
              </Field>
            </div>
          )}

          {/* Step 1: Admin User */}
          {step === 1 && (
            <div>
              <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '17px' }}>Admin User</h3>
              <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '-12px', marginBottom: '20px' }}>
                This person will be the company Owner — they can invite brokers and manage all settings.
              </p>
              <Field label="Full Name *">
                <input value={admin.name} onChange={e => setA('name', e.target.value)} placeholder="Jane Smith" style={inputStyle} />
              </Field>
              <Field label="Email Address *">
                <input type="email" value={admin.email} onChange={e => setA('email', e.target.value)} placeholder="jane@acmemortgages.co.uk" style={inputStyle} />
              </Field>
              <Field label="Phone">
                <input type="tel" value={admin.phone} onChange={e => setA('phone', e.target.value)} placeholder="+44 7700 900000" style={inputStyle} />
              </Field>
              <Field label="Password *">
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={admin.password}
                    onChange={e => setA('password', e.target.value)}
                    placeholder="Min 8 characters"
                    style={{ ...inputStyle, paddingRight: '80px' }}
                  />
                  <button onClick={() => setShowPassword(s => !s)} type="button" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px' }}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </Field>
              <Field label="Confirm Password *">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={admin.confirmPassword}
                  onChange={e => setA('confirmPassword', e.target.value)}
                  placeholder="Re-enter password"
                  style={{ ...inputStyle, borderColor: admin.confirmPassword && admin.password !== admin.confirmPassword ? '#ef4444' : '#d1d5db' }}
                />
              </Field>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && (
            <div>
              <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '17px' }}>Review & Confirm</h3>
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                <ReviewRow label="Company Name" value={company.name} />
                {company.website && <ReviewRow label="Website" value={company.website} />}
                <ReviewRow label="Plan" value={company.complimentary ? 'Complimentary — founding member (no billing)' : 'Standard — £10 per user / month'} />
                {!company.complimentary && (
                  <ReviewRow label="Free Trial" value={company.trial_months > 0 ? `${company.trial_months} month${company.trial_months > 1 ? 's' : ''}` : 'None'} />
                )}
                {company.referral_code && <ReviewRow label="Referral Code" value={company.referral_code} />}
              </div>
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px' }}>
                <ReviewRow label="Admin Name" value={admin.name} />
                <ReviewRow label="Admin Email" value={admin.email} />
                {admin.phone && <ReviewRow label="Admin Phone" value={admin.phone} />}
                <ReviewRow label="Password" value="••••••••" />
              </div>
              <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '16px', lineHeight: 1.6 }}>
                Clicking "Create Company" will create the company record, admin user, and seed default document types.
                The admin can log in immediately — they'll be asked to accept the BrokerConnect Onboarding Agreement
                before they can use the platform.
              </p>
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '28px' }}>
            <button onClick={() => { setError(''); setEmailConflict(null); setStep(s => Math.max(0, s - 1)); }} style={btnOutline} disabled={step === 0}>
              ← Back
            </button>
            {step < 2 ? (
              <button onClick={next} style={btnPrimary}>
                Next →
              </button>
            ) : (
              <button onClick={submit} disabled={saving} style={{ ...btnPrimary, background: saving ? '#93c5fd' : '#1e40af' }}>
                {saving ? 'Creating…' : 'Create Company'}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>{label}</label>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e5e7eb' }}>
      <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#374151', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' };
const btnPrimary = { background: '#1e40af', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', cursor: 'pointer', fontWeight: 600 };
const btnOutline = { background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', cursor: 'pointer', fontWeight: 500 };
